import React from 'react';
import {
  DesignInteractionEnum,
  DesignSourceEnum,
} from '@insights/insights-data-provider';
import { OpenDesignCodeStateEnum } from '@inter-ikea-kompis/component-open-design-code';
import fixOldVpc from '../services/FixVPC';
import pacValid from '../services/PacValidator';
import localStatisticsReporter from '../services/statistics/insights/custom/local/localStatisticsReporter';
import mandatoryStatisticsReporter from '../services/statistics/insights/mandatory/mandatoryStatisticsReporter';
import { getVpcService } from '../services/ServiceHandler';

const useVpc = ({
  successCallback = ({ pac }) => {},
  failCallback = () => {},
}) => {
  const [loadRes, setLoadRes] = React.useState(null);
  const [loadingVpcState, setLoadingVpcState] = React.useState(
    OpenDesignCodeStateEnum.default
  );

  const resolveFunctions = {
    [OpenDesignCodeStateEnum.default]: successCallback,
    [OpenDesignCodeStateEnum.failed]: failCallback,
  };

  React.useEffect(() => {
    loadRes && resolveFunctions[loadingVpcState](loadRes);
  }, [loadingVpcState, loadRes]);

  /**
   * Handles successfull loading of VPC
   *
   * @param vpc
   * @param pac
   */
  const handleSuccess = (vpc, pac) => {
    setLoadingVpcState(OpenDesignCodeStateEnum.default);
    setLoadRes({ vpc, pac });
    mandatoryStatisticsReporter.reportInitialPlanningSession(
      vpc.configurationId,
      DesignSourceEnum.enterCode
    );
    mandatoryStatisticsReporter.reportDesignInteractionEvent(
      vpc.configurationId,
      DesignInteractionEnum.getConfiguration
    );
    localStatisticsReporter.reportStartViewVpcEnterCodeSuccess(
      vpc.configurationId
    );
  };

  /**
   * Handles any failure to load VPC
   *
   * @param err
   */
  const handleFailure = err => {
    setLoadingVpcState(OpenDesignCodeStateEnum.failed);
    setLoadRes(err);
  };

  /**
   * Check pac validity
   *
   * @param pac
   * @param vpcConfig
   * @returns {*}
   */
  const handlePacValidity = (pac, vpcConfig) =>
    pacValid(pac)
      ? handleSuccess(vpcConfig, pac)
      : handleFailure('Pac invalid');

  /**
   * Handle VPC response
   *
   * @param vpcConfig
   */
  const handleResponse = vpcConfig =>
    handlePacValidity(fixOldVpc(vpcConfig.configuration), vpcConfig);

  /**
   * Initiates fetching of VPC
   *
   * @param vpc
   * @returns {Promise<T>}
   */
  const fetchVpc = async vpc => {
    setLoadRes(null);
    setLoadingVpcState(OpenDesignCodeStateEnum.loading);

    return await getVpcService()
      .getConfiguration(vpc)
      .then(handleResponse)
      .catch(handleFailure);
  };

  /**
   * Reset hook and it's data
   */
  const resetHook = () => {
    setLoadRes(null);
    setLoadingVpcState(OpenDesignCodeStateEnum.default);
  };

  return {
    vpcLoadingState: loadingVpcState,
    fetchVpc,
    resetHook,
  };
};

export default useVpc;
